import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { v4 } from "uuid";
import toast from "react-hot-toast";
import { ChevronDown, ExternalLink } from "lucide-react";
import { getDocData, setDocData } from "../../Firebase/Firebase.utils";
import { COLLECTIONS, setPublishStatus } from "../utils/cmsSchema";
import { resolveUniqueSlug } from "../utils/slugUniqueness";
import { slugify } from "../utils/slugify";
import { revalidateFor } from "../utils/revalidatePublic";
import FormShell, {
  FormSection,
  PublishToggle,
  TextArea,
  TextField,
} from "../components/FormShell";
import RichTextEditor from "../components/RichTextEditor";
import FileDropzone from "../components/FileDropzone";

// Edit form for one leader profile in the `leaders` collection. Fields
// match the shape read by the public /executive-leadership pages.
//
// Fields:
//   - name, position               → card + detail hero
//   - shortBio                     → card preview, meta description default
//   - fullBio                      → detail page body (Tiptap-editable)
//   - photoUrl                     → FileDropzone upload; hero/card image
//   - photoObjectPosition          → CSS object-position hack for
//                                    face-cropping (e.g. "0px -10px")
//   - linkedInUrl, email           → detail sidebar CTAs
//   - sortOrder                    → manual ordering on /executive-leadership
//   - slug                         → auto from name, editable
//   - metaTitle, metaDescription   → per-profile SEO

const SCHEMA = COLLECTIONS.leaders;

export default function LeaderForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userObject);

  const isNew = !id;
  const docId = useMemo(() => id || v4(), [id]);

  const [form, setForm] = useState({
    name: "",
    position: "",
    shortBio: "",
    fullBio: "",
    photoUrl: "",
    photoObjectPosition: "",
    linkedInUrl: "",
    email: "",
    sortOrder: 0,
    slug: "",
    metaTitle: "",
    metaDescription: "",
    published: false,
  });
  const [originalDoc, setOriginalDoc] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [seoOpen, setSeoOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(!isNew);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingCount, setUploadingCount] = useState(0);

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const trackUpload = (b) =>
    setUploadingCount((c) => Math.max(0, c + (b ? 1 : -1)));

  useEffect(() => {
    if (isNew) return;
    let active = true;
    (async () => {
      const data = await getDocData(SCHEMA.key, id);
      if (!active) return;
      if (!data) {
        toast.error("Not found.");
        navigate(SCHEMA.route);
        return;
      }
      setOriginalDoc(data);
      setForm({
        name: data.name || "",
        position: data.position || "",
        shortBio: data.shortBio || "",
        fullBio: data.fullBio || "",
        photoUrl: data.photoUrl || "",
        photoObjectPosition: data.photoObjectPosition || "",
        linkedInUrl: data.linkedInUrl || "",
        email: data.email || "",
        sortOrder: Number.isFinite(data.sortOrder) ? data.sortOrder : 0,
        slug: data.slug || "",
        metaTitle: data.metaTitle || "",
        metaDescription: data.metaDescription || "",
        published: data[SCHEMA.statusField] === SCHEMA.publishedValue,
      });
      setSlugTouched(!!data.slug);
      setIsLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [id, isNew, navigate]);

  useEffect(() => {
    if (slugTouched) return;
    if (!form.name) return;
    setForm((p) => ({ ...p, slug: slugify(form.name) }));
  }, [form.name, slugTouched]);

  const handleSave = async (publishOverride) => {
    if (!form.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    if (uploadingCount > 0) {
      toast.error("Wait for the photo upload to finish.");
      return;
    }
    setIsSaving(true);
    try {
      const finalSlug = await resolveUniqueSlug({
        collectionName: SCHEMA.key,
        desiredSlug: form.slug,
        fallbackTitle: form.name,
        ignoreId: docId,
      });
      const nowIso = new Date().toISOString();
      const published =
        typeof publishOverride === "boolean" ? publishOverride : form.published;
      const trimmedName = form.name.trim();
      const sortOrderNumeric = Number(form.sortOrder);
      const payload = {
        ...(originalDoc || {}),
        name: trimmedName,
        // `title` mirror so ContentCard picks up the display name via
        // its shared `doc.title` accessor.
        title: trimmedName,
        titleLower: trimmedName.toLowerCase(),
        position: form.position.trim(),
        shortBio: form.shortBio.trim(),
        // Mirror shortBio → description for the CMS card preview line
        // (ContentCard reads doc.description).
        description: form.shortBio.trim(),
        fullBio: form.fullBio || "",
        photoUrl: form.photoUrl || "",
        photoObjectPosition: form.photoObjectPosition.trim(),
        linkedInUrl: form.linkedInUrl.trim(),
        email: form.email.trim(),
        sortOrder: Number.isFinite(sortOrderNumeric) ? sortOrderNumeric : 0,
        slug: finalSlug,
        metaTitle: form.metaTitle.trim(),
        metaDescription: form.metaDescription.trim(),
        updatedAt: nowIso,
        lastUpdatedBy: currentUser?.email || null,
      };
      setPublishStatus(payload, SCHEMA, published);
      if (isNew) {
        payload.createdAt = nowIso;
        payload.createdBy = currentUser?.email || null;
        payload.type = SCHEMA.key;
      }
      await setDocData(SCHEMA.key, docId, payload);
      revalidateFor(SCHEMA.key);
      toast.success(
        published ? "Leader published." : "Leader saved.",
      );
      setForm((p) => ({ ...p, slug: finalSlug, published }));
      if (isNew) {
        navigate(`${SCHEMA.route}/${docId}`, { replace: true });
      } else {
        setOriginalDoc(payload);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save.");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="text-sm text-gray-500">Loading…</div>;
  }

  const saveDisabled = isSaving || uploadingCount > 0;
  const publicUrl = `https://www.leechiu.com/executive-leadership/${
    form.slug || docId
  }`;

  return (
    <FormShell
      backTo={SCHEMA.route}
      backLabel="Back to Executive Leadership"
      eyebrow={isNew ? "New Leader" : "Editing Leader"}
      title={form.name || "Untitled Leader"}
      belowTitle={
        !isNew ? (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-leechiuBlue hover:underline"
          >
            View public profile
            <ExternalLink className="w-3 h-3" />
          </a>
        ) : null
      }
      footer={
        <>
          <button
            type="button"
            onClick={() => navigate(SCHEMA.route)}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleSave(false)}
            disabled={saveDisabled}
            className="px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm disabled:opacity-50"
          >
            Save Draft
          </button>
          <button
            type="button"
            onClick={() => handleSave(true)}
            disabled={saveDisabled}
            className="px-4 py-2 rounded-lg bg-leechiuOrange text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {form.published ? "Update & Keep Published" : "Publish"}
          </button>
        </>
      }
    >
      <div className="lg:col-span-2 space-y-6">
        <FormSection
          title="Profile"
          description="Basic identity shown on the card and detail page."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              label="Full name"
              required
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              placeholder="e.g. David Leechiu"
            />
            <TextField
              label="Position"
              value={form.position}
              onChange={(e) => setField("position", e.target.value)}
              placeholder="e.g. CEO, Executive Director"
            />
          </div>
          <TextArea
            label="Short bio"
            rows={3}
            value={form.shortBio}
            onChange={(e) => setField("shortBio", e.target.value)}
            placeholder="1-2 sentences shown on the /executive-leadership card and used as the SEO description fallback."
            hint="Recommended: 120–200 characters."
          />
        </FormSection>

        <FormSection
          title="Full bio"
          description="Rich content shown on the individual /executive-leadership/<slug> page. Supports headings, lists, and links."
        >
          <RichTextEditor
            value={form.fullBio}
            onChange={(html) => setField("fullBio", html)}
            collection={SCHEMA.key}
            docId={docId}
            placeholder="Tell their story — background, key deals, expertise…"
            onUploadingChange={trackUpload}
          />
        </FormSection>

        <FormSection
          title="Contact"
          description="Optional. Shown as buttons on the profile detail page."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TextField
              label="LinkedIn URL"
              type="url"
              value={form.linkedInUrl}
              onChange={(e) => setField("linkedInUrl", e.target.value)}
              placeholder="https://linkedin.com/in/…"
            />
            <TextField
              label="Email (public)"
              type="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder="firstname.lastname@leechiu.com"
              hint="Only include if the leader is happy to receive direct email from visitors."
            />
          </div>
        </FormSection>

        <FormSection
          title={
            <button
              type="button"
              onClick={() => setSeoOpen((s) => !s)}
              className="flex items-center gap-1 text-sm font-semibold text-leechiuBlue"
            >
              SEO &amp; URL
              <ChevronDown
                className={`w-4 h-4 transition-transform ${
                  seoOpen ? "" : "-rotate-90"
                }`}
              />
            </button>
          }
          description="Controls the public URL, browser tab title, and search-result snippet. Also drives the schema.org/Person structured data."
        >
          {seoOpen ? (
            <>
              <TextField
                label="Profile URL"
                value={form.slug}
                onChange={(e) => {
                  setSlugTouched(true);
                  setField("slug", e.target.value);
                }}
                onBlur={(e) => {
                  const formatted = slugify(e.target.value);
                  if (formatted !== e.target.value) {
                    setField("slug", formatted);
                  }
                }}
                placeholder="auto-generated-from-name"
                hint="Paste any phrase; formatted on blur. Leave blank to auto-generate from the name. Uniqueness checked on save."
              />
              <TextField
                label="Page Title"
                value={form.metaTitle}
                onChange={(e) => setField("metaTitle", e.target.value)}
                placeholder="Shown in browser tab and search results"
                hint="Recommended: 50–60 characters. Falls back to Name — Position if blank."
              />
              <TextArea
                label="Meta Description"
                rows={3}
                value={form.metaDescription}
                onChange={(e) => setField("metaDescription", e.target.value)}
                placeholder="Short summary for search results and social shares"
                hint="Recommended: 140–160 characters. Falls back to short bio."
              />
            </>
          ) : null}
        </FormSection>
      </div>

      <div className="space-y-6">
        <FormSection
          title="Photo"
          description="Portrait shown on the card and detail hero. Portrait orientation (4:5) recommended."
        >
          <FileDropzone
            kind="image"
            collection={SCHEMA.key}
            docId={docId}
            value={form.photoUrl}
            onChange={(url) => setField("photoUrl", url)}
            onUploadingChange={trackUpload}
          />
          <TextField
            label="Photo object position (optional)"
            value={form.photoObjectPosition}
            onChange={(e) => setField("photoObjectPosition", e.target.value)}
            placeholder="center top"
            hint='Nudges how the photo crops inside the frame — e.g. "0px -10px" to push the face down slightly. Defaults to "center top" if empty.'
          />
        </FormSection>

        <FormSection title="Visibility">
          <PublishToggle
            value={form.published}
            onChange={(v) => setField("published", v)}
          />
        </FormSection>

        <FormSection
          title="Ordering"
          description="Lower numbers appear first on the public /executive-leadership grid. Use 0 for CEO, 10 for executive directors, etc."
        >
          <TextField
            label="Sort order"
            type="number"
            value={form.sortOrder}
            onChange={(e) => setField("sortOrder", e.target.value)}
            placeholder="0"
          />
        </FormSection>
      </div>
    </FormShell>
  );
}
